/**
 * NpcInteractModule — talk to NPCs (T near one; E stays place-object).
 *
 * MORROWIND-STYLE topic dialogue (2026-08-11): the window is TES III's —
 * dialogue text on the left with blue clickable keywords, the topic list down
 * the right, disposition (player_standing regard, server-computed) under the
 * name, Goodbye at the bottom of the topic column. The topic engine
 * (lib/topic_dialogue) answers INSTANTLY and offline from the replicated sim
 * tables, so versonas are aware of what they're doing (versona_state
 * activity), where they stand (nearest named town slot), the rumors they
 * personally carry (versona_memory kind='rumor'), the war, offices, prices —
 * and of the PLAYER (standing tier, residency, remembered past talks).
 *
 * Typed free text goes through the NLP matcher first (stemmed, typo-tolerant
 * keyword scoring — instant when it lands on a topic, and the topic joins the
 * list as DISCOVERED); only unmatched questions escalate to the LLM inbox
 * (sayToVersona). When no LLM is attached (no reply ever lands), the module
 * latches llmDown and answers unmatched questions with a polite redirect to
 * real topics — the whole window keeps working with zero LLM.
 *
 * Pre-publish safe: the sim tables are accessed through narrow casts and
 * every path no-ops until the server is published + bindings regenerated.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { pickConversant } from '../lib/npc_pick.js';
import { enterables } from '../lib/building_interiors.js';
import { relNetOf as relNetOfC, tierOf as tierOfC, decayedWeight as decayedW, agoBucket, ASPIRATION_LABEL, dispositionOf as dispositionOfC } from '../lib/relationship.js';
import { civilizationOf, type Civilization } from '../lib/biome_settlements.js';
import { civOfSettlementRowC } from '../lib/civ_row.js';
import { avatarFor } from './EnvironmentModule.js';
import { voiceFor } from '../lib/tts/versona_voice.js';
import { versonaTts } from '../lib/tts/versona_tts.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { townSlots, type TownSlot } from '../../../shared/town_layout.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { todBucket, feelingToneOf } from '../lib/npc_dialogue.js';
import {
  availableTopics, composeGreeting, linkify, matchTopic, nearTopics, respond,
  standingTierOf, topicByKey, type TopicFacts,
} from '../lib/topic_dialogue.js';
import { nameFromTables } from '../lib/versona_names.js';
import { resolvedCityName } from '../lib/city_names.js';
import { NpcTalkThreads } from './NpcTalkThreads.js';

/** Local FNV-1a — shuffle-bag variety for dialogue lines (deterministic). */
function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

interface VersonaRow { versonaId: bigint; worldId: bigint; archetype: string; curX: number; curZ: number; state: string }
interface HotRow { versonaId: bigint; worldId: bigint; curX: number; curZ: number; state: string }
interface AgentRow { versonaId: bigint; settlementId: bigint; job: string; hunger: number; mood: number; diligence: number; boldness: number }
interface EconRow { settlementId: bigint; priceFood: number; projectKey: string; mayorId: bigint; taxBps: number }
interface EventRow { eventId: bigint; settlementId: bigint; kind: string; detail: string }
interface NormRow { normId: bigint; settlementId: bigint; text: string; active: boolean }
interface MemoryRow { memoryId: bigint; versonaId: bigint; kind: string; text: string; importance: number; at: { microsSinceUnixEpoch: bigint } }
interface BeliefRow { beliefId: bigint; versonaId: bigint; kind: string; subjectKey: string; text: string; weight: number; confidence: number }
interface ThoughtRow { versonaId: bigint; text: string }
interface EmotionRow { emotionId: bigint; versonaId: bigint; etype: string; intensity: number; causeText: string }
interface ChatRow { chatId: bigint; versonaId: bigint; speaker: string; speakerName: string; text: string }
interface StandingRow { identity: { toHexString(): string }; settlementId: bigint; regard: number; updatedAt: { microsSinceUnixEpoch: bigint } }
interface WarRow { aggressorSid: bigint; defenderSid: bigint; status: string; exhaustionA: number; exhaustionB: number; casualties: number }
interface OfficeRow { settlementId: bigint; office: string; holderName: string }
interface ResidencyRow { identity: { toHexString(): string }; settlementId: bigint }
interface MemberRow { householdId: bigint; versonaId: bigint }
interface HouseholdRow { householdId: bigint; surname: string }
/** Editor-placed versona (Edit Mode → Versona). `custom` = its context IS this row (private/new worlds). */
interface ProfileRow {
  versonaId: bigint; worldId: bigint; custom: boolean; name: string; archetype: string; persona: string; context: string;
  greeting: string; deflect: string; topicsJson: string; llm: boolean; strict: boolean; temperature: number; maxWords: number; wanderM: number;
}
interface CustomTopic { k: string; l: string; r: string }

const TALK_RANGE_M = 6;
const VENUE_NEAR_M = 30;
const PROJECT_LABELS: Record<string, string> = { fountain: 'a plaza fountain', kiosk: 'new market stalls', lamps: 'street lighting', bench: 'plaza seating' };

// ── Civilization identity (mirror of the server derivation) ──────────────────
const CIV_DISPLAY: Record<Civilization, { name: string; demonym: string }> = {
  a: { name: 'Aurelian Concord', demonym: 'Aurelian' },
  b: { name: 'Borean Compact', demonym: 'Borean' },
};
const WORKPLACE_NOUN: Record<string, string> = {
  farmer: 'growing terraces', miner: 'ore works', smith: 'fabrication halls',
  merchant: 'trade concourse', guard: 'watch post', bard: 'leisure spire', scout: 'survey annex',
};

/** localStorage-backed string set (topic discovery + met-NPC memory). */
function loadSet(key: string): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(key) ?? '[]') as string[]); } catch { return new Set(); }
}
function saveSet(key: string, s: Set<string>, cap: number): void {
  try { localStorage.setItem(key, JSON.stringify([...s].slice(-cap))); } catch { /* private mode */ }
}

export class NpcInteractModule implements IModule {
  readonly name = 'NpcInteract';
  private win: UiWindow | null = null;
  private log: HTMLDivElement | null = null;
  private head: HTMLDivElement | null = null;
  private dispFill: HTMLDivElement | null = null;
  private dispLabel: HTMLSpanElement | null = null;
  private topicsPane: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private current: bigint | null = null;
  private bag = 0;
  private waitingSince = 0;
  private renderedChat = new Set<string>();
  /** Topics the player has discovered via links/typing (Morrowind's growing list). */
  private discovered = loadSet('atlas:topics');
  /** Versonas already spoken to (the "Back again" recognition). */
  private met = loadSet('atlas:met');
  /** Latched true once an unmatched question got no LLM reply — later ones
   *  answer instantly with a topic redirect instead of a 10 s shrug. */
  private llmDown = false;
  private justDiscovered = '';
  /** The dialogue PATHS layered over the topic window (openers, reply chips, stories, tales, leads). */
  private threads: NpcTalkThreads | null = null;

  private offs: Array<() => void> = [];
  private timers = new Set<number>();
  /** Tracked timeout — cleared on dispose (they used to outlive the module). */
  private timer(fn: () => void, ms: number): void {
    const id = window.setTimeout(() => { this.timers.delete(id); fn(); }, ms);
    this.timers.add(id);
  }
  private onKeyDownT = (e: KeyboardEvent): void => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || (t instanceof Element && t.closest('.cm-editor')))) return;
    if (e.code === 'KeyT' && !e.ctrlKey && !e.metaKey && !e.repeat) { // an OS auto-repeat is not a second press (2026-09-23)
      // Aboard the sky-ship the CREW own the T key (SkyShipModule handles it
      // in capture phase) — nearestVersona is XZ-only and would target a
      // townsperson 760 m below the deck.
      if (atlas.interiorId?.startsWith('skyship:')) return;
      const near = this.nearestVersona();
      if (near) { e.preventDefault(); this.open(near); }
    }
  };

  /**
   * Open the conversation window for a SKY-SHIP crew member (mega-ship wave):
   * a purely client-side speaker — no versona row, no lore facts — with a
   * hand-authored greeting + topic set (the editor-versona custom-topic
   * rendering, reused).
   */
  openShipCrew(opts: { name: string; title: string; greeting: string; topics: Array<{ k: string; l: string; r: string }> }): void {
    this.current = null;
    this.renderedChat.clear();
    if (!this.win) this.buildWindow();
    if (this.head) {
      this.head.textContent = `${opts.name} — ${opts.title}`;
      this.head.title = opts.title;
    }
    if (this.dispFill) this.dispFill.parentElement!.style.visibility = 'hidden';
    if (this.dispLabel) this.dispLabel.textContent = '';
    if (this.log) this.log.textContent = '';
    this.win!.setOpen(true);
    this.npcParagraph(opts.greeting, null, undefined);
    const pane = this.topicsPane;
    if (!pane) return;
    pane.textContent = '';
    const cap = document.createElement('div');
    cap.textContent = 'TOPICS';
    cap.style.cssText = 'color:var(--ax-muted);font:600 10px var(--ax-font);letter-spacing:1px;padding:2px 4px';
    pane.appendChild(cap);
    for (const t of opts.topics) {
      const b = document.createElement('button');
      b.textContent = t.l;
      b.dataset.topic = t.k;
      b.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-hi)';
      b.onmouseenter = () => { b.style.background = 'var(--ax-bg3)'; };
      b.onmouseleave = () => { b.style.background = 'none'; };
      b.onclick = () => { this.playerLine(t.l); this.npcParagraph(t.r, null, undefined); };
      pane.appendChild(b);
    }
    const gap = document.createElement('div');
    gap.style.cssText = 'flex:1';
    pane.appendChild(gap);
    const bye = document.createElement('button');
    bye.textContent = 'Goodbye';
    bye.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:600 12px var(--ax-font);color:var(--ax-text)';
    bye.onclick = () => { this.win?.setOpen(false); };
    pane.appendChild(bye);
  }

  private app: App | null = null;

  init(app: App): void {
    this.app = app;
    void app;
    window.addEventListener('keydown', this.onKeyDownT);
    this.threads = new NpcTalkThreads({
      tbl: <T,>(name: string): Iterable<T> => this.tbl<T>(name),
      npcSay: (text, spoken = true) => { const v = this.currentRow(); this.npcParagraph(text, v ? this.buildFacts(v) : null, undefined, spoken); },
      playerSay: (text) => this.playerLine(text),
      system: (text) => this.systemLine(text),
      logEl: () => this.log,
      discover: (key) => this.discover(key),
      refresh: () => { const v = this.currentRow(); if (v) { const f = this.buildFacts(v); this.renderHeader(f, v); this.renderTopics(f); } },
      townName: (sid) => { const site = this.siteOf(sid); return site ? resolvedCityName(site, atlas.conn) : ''; },
    });
    this.threads.bindFacts(() => { const v = this.currentRow(); return v && this.threads ? this.threads.facts(this.buildFacts(v), v.versonaId) : null; });
    // Dev hook (probes): open the talk window for a versona by id, bypassing
    // the walk-up — the OPEN self-row teleport-freeze SDK bug (see memory) can
    // strand a headless probe's LOCAL position even after the server-side
    // teleport landed, so proximity can't be staged reliably headless.
    (window as unknown as { __talk?: unknown }).__talk = {
      /** Issue the lazy society subscriptions EARLY (probes: a saturated
       *  headless tab starves outbound WS sends after world entry — the
       *  subscribe request itself is a send, so it must go out while the
       *  socket is calm; the sync always arrives, inbound stays live). */
      warm: (): void => this.ensureSocietySubs(),
      open: (id: number | string | bigint): boolean => {
        const conn = atlas.conn;
        if (!conn) return false;
        const vid = BigInt(id);
        for (const row of conn.db.versona.iter() as Iterable<VersonaRow>) {
          if (row.versonaId === vid) { this.open(row); return true; }
        }
        return false;
      },
    };
    this.offs.push(bus.on('atlas:world-cleared', () => this.clearNpcSubs())); // stale after a hop, still evaluated per transaction
    this.offs.push(bus.on('atlas:account-ready', () => {
      const conn = atlas.conn;
      try {
        // npc_event is NOT here: at 30 events × 312 settlements it is a ~9k-row
        // initial sync every client paid at join for a table only the Talk
        // panel (newest-per-settlement) and the admin inspector ever read — it
        // rides the lazy society subs below instead.
        // (bug sweep) versona_chat rides the per-NPC lazy sub now — the
        // whole-table copy was paid by EVERY client at join, before any
        // Talk window ever opened (NpcUnit keeps its own scoped sub).
        conn?.subscriptionBuilder().subscribe(['SELECT * FROM settlement_econ']);
        const chat = (conn?.db as unknown as { versonaChat?: { onInsert(f: (c: unknown, row: ChatRow) => void): void } }).versonaChat;
        chat?.onInsert((_c, row) => this.onChatRow(row));
      } catch { /* pre-publish server — talk window works template-only */ }
    }));
  }

  // ── data helpers (cast-based: bindings may predate the publish) ─────────────
  private tbl<T>(name: string): Iterable<T> {
    const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
    try { return t ? t.iter() : []; } catch { return []; }
  }

  /** The CUSTOM profile of a versona (null for townsfolk and Commons placements — they use the society context). */
  private customProfile(versonaId: bigint): ProfileRow | null {
    for (const p of this.tbl<ProfileRow>('versonaProfile')) if (p.versonaId === versonaId) return p.custom ? p : null;
    return null;
  }

  private customTopics(p: ProfileRow): CustomTopic[] {
    try {
      const raw = JSON.parse(p.topicsJson || '[]') as Array<Partial<CustomTopic>>;
      return Array.isArray(raw) ? raw.filter((t) => t && typeof t.l === 'string' && typeof t.r === 'string').map((t) => ({ k: String(t.k ?? t.l), l: t.l!, r: t.r! })) : [];
    } catch { return []; }
  }

  /** Typed text → an authored topic (label words / key), or null. */
  private matchCustomTopic(text: string, topics: CustomTopic[]): CustomTopic | null {
    const low = text.toLowerCase();
    const words = new Set(low.split(/[^a-z0-9]+/).filter((w) => w.length > 2));
    let best: CustomTopic | null = null;
    let bestScore = 0;
    for (const t of topics) {
      const lw = t.l.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);
      let score = low.includes(t.l.toLowerCase()) ? 3 : 0;
      for (const w of lw) if (words.has(w)) score += 1;
      if (score > bestScore) { bestScore = score; best = t; }
    }
    return bestScore >= 1 ? best : null;
  }

  private nearestVersona(): VersonaRow | null {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return null;
    const hot = new Map<string, HotRow>();
    for (const h of this.tbl<HotRow>('versonaState')) hot.set(h.versonaId.toString(), h);
    // The one you are FACING within range (the look cone), else the nearest (npc_pick, 2026-09-15).
    // THE INDOOR-TARGET LAW (2026-09-22): a versona seated in another building
    // (or outside while you are in one) is behind a wall — never a target.
    const env = this.app?.getModule('Environment') as { versonaIndoors?: (id: string) => string | null | undefined } | undefined;
    const myRoom = atlas.interiorId !== null && enterables.has(atlas.interiorId) ? atlas.interiorId : null;
    const rows: VersonaRow[] = [];
    const cands: Array<{ x: number; z: number }> = [];
    for (const v of conn.db.versona.iter() as Iterable<VersonaRow>) {
      if (v.worldId !== wid) continue;
      const room = env?.versonaIndoors?.(v.versonaId.toString());
      if (room !== undefined && room !== myRoom) continue;
      const h = hot.get(v.versonaId.toString());
      const x = h ? h.curX : v.curX, z = h ? h.curZ : v.curZ;
      if (Math.hypot(x - atlas.localX, z - atlas.localZ) > TALK_RANGE_M) continue;
      rows.push(v); cands.push({ x, z });
    }
    const i = pickConversant(atlas.localX, atlas.localZ, atlas.localYaw, cands, TALK_RANGE_M);
    return i >= 0 ? rows[i] : null;
  }

  // ── settlement geography (sites + named slots, cached) ─────────────────────
  private seedCache = '';
  private sitesCache: SettlementSite[] | null = null;
  private slotsCache = new Map<number, TownSlot[]>();

  private commonsSeed(): string {
    if (this.seedCache === '') {
      const conn = atlas.conn;
      if (conn) for (const w of conn.db.world.iter()) if (w.kind === 'commons') { this.seedCache = w.terrainSeed; break; }
    }
    return this.seedCache;
  }

  private siteOf(settlementId: bigint): SettlementSite | null {
    if (settlementId <= 0n) return null;
    const seed = this.commonsSeed();
    if (seed === '') return null;
    if (!this.sitesCache) this.sitesCache = settlementSites(seed);
    return this.sitesCache.find((st) => st.id === Number(settlementId - 1n)) ?? null;
  }

  private slotsOf(site: SettlementSite): TownSlot[] {
    let s = this.slotsCache.get(site.id);
    if (!s) { s = townSlots(this.commonsSeed(), site); this.slotsCache.set(site.id, s); }
    return s;
  }

  // ── the facts assembly — everything the topic engine is aware OF ───────────
  private buildFacts(v: VersonaRow): TopicFacts {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    let agent: AgentRow | null = null;
    for (const a of this.tbl<AgentRow>('npcAgent')) if (a.versonaId === v.versonaId) { agent = a; break; }
    let econ: EconRow | null = null;
    if (agent) for (const e of this.tbl<EconRow>('settlementEcon')) if (e.settlementId === agent.settlementId) { econ = e; break; }
    let news = '';
    if (agent) {
      let bestId = -1n;
      for (const ev of this.tbl<EventRow>('npcEvent')) {
        if (ev.settlementId === agent.settlementId && ev.eventId > bestId) { bestId = ev.eventId; news = ev.detail; }
      }
    }
    let custom = '';
    if (agent) {
      let bestNorm = -1n;
      for (const n of this.tbl<NormRow>('settlementNorm')) {
        if (n.settlementId === agent.settlementId && n.active && n.normId > bestNorm) { bestNorm = n.normId; custom = n.text; }
      }
    }
    // Memories: the most important line + the hearsay they personally carry.
    let memoryLine = '';
    let bestImp = 0;
    const rumorRows: Array<{ id: bigint; text: string }> = [];
    for (const m of this.tbl<MemoryRow>('versonaMemory')) {
      if (m.versonaId !== v.versonaId) continue;
      if (m.importance > bestImp) { bestImp = m.importance; memoryLine = m.text; }
      if (m.kind === 'rumor') rumorRows.push({ id: m.memoryId, text: m.text });
    }
    rumorRows.sort((a, b) => (a.id > b.id ? -1 : 1));
    const rumors = rumorRows.slice(0, 3).map((r) => r.text);
    // Dominant typed emotion → tone + answerable cause (R11 composure mirror:
    // moderate negative feelings stay behind the face; strong ones leak).
    const NEGATIVE_DISPLAY = new Set(['distress', 'fear', 'shame', 'remorse', 'anger', 'resentment', 'reproach', 'dislike', 'gloating', 'fears_confirmed', 'disappointment']);
    const composure = 55 + Math.round(((agent?.diligence ?? 50) * 0.7 + (100 - (agent?.boldness ?? 50)) * 0.3) / 8);
    let feelingTone: TopicFacts['feelingTone'] = '';
    let feelingCause = '';
    let bestEmo = 24;
    for (const em of this.tbl<EmotionRow>('versonaEmotion')) {
      if (em.versonaId === v.versonaId && em.intensity > bestEmo) {
        if (NEGATIVE_DISPLAY.has(em.etype) && em.intensity < 80 && em.intensity < composure) continue;
        const tone = feelingToneOf(em.etype);
        if (tone !== '') { bestEmo = em.intensity; feelingTone = tone; feelingCause = em.causeText; }
      }
    }
    this.lastTone = feelingTone; // the life renderer's talking pose reads this
    // Sky: time of day + live weather.
    let tod = 0.5;
    let weather = '';
    if (conn && wid !== null) {
      const sky = (conn.db as unknown as { skyState: { worldId: { find(w: bigint): { timeOfDay: number; weather: string } | undefined } } }).skyState.worldId.find(wid);
      tod = sky?.timeOfDay ?? 0.5;
      weather = sky?.weather ?? '';
    }
    // Geography: home site → town name, civ voice, nearest named venue.
    const site = agent ? this.siteOf(agent.settlementId) : null;
    // ROW-FIRST (conquest law): a flipped settlement speaks with its NEW civ's
    // voice — names, demonyms, venue names, rival talk.
    const civ: Civilization | null = site && agent ? civOfSettlementRowC(agent.settlementId, site.siteSeed) : null;
    const civInfo = civ ? CIV_DISPLAY[civ] : null;
    const town = site ? resolvedCityName(site, conn) : '';
    let venue = '';
    let venueFn = '';
    if (site) {
      const hot = [...this.tbl<HotRow>('versonaState')].find((h) => h.versonaId === v.versonaId);
      const nx = hot ? hot.curX : v.curX, nz = hot ? hot.curZ : v.curZ;
      let bd = VENUE_NEAR_M * VENUE_NEAR_M;
      for (const sl of this.slotsOf(site)) {
        const d2 = (sl.x - nx) ** 2 + (sl.z - nz) ** 2;
        if (d2 < bd) { bd = d2; venue = civ === 'b' ? sl.nameB : sl.nameA; venueFn = sl.fn; }
      }
    }
    // Offices + the keeper.
    let keeperName = '';
    let guildmasterName = '';
    if (agent) {
      for (const o of this.tbl<OfficeRow>('settlementOffice')) {
        if (o.settlementId !== agent.settlementId) continue;
        if (o.office === 'keeper') keeperName = o.holderName;
        if (o.office === 'guildmaster') guildmasterName = o.holderName;
      }
    }
    const mayorName = econ && econ.mayorId > 0n ? nameFromTables(conn, econ.mayorId) : keeperName;
    // War, as their town lives it.
    let atWar = false;
    let warFoe = '';
    let warCasualties = 0;
    let warWeary = false;
    if (agent) {
      for (const w of this.tbl<WarRow>('warState')) {
        if (w.status !== 'active') continue;
        const mine = w.aggressorSid === agent.settlementId ? 'a' : w.defenderSid === agent.settlementId ? 'd' : '';
        if (mine === '') continue;
        atWar = true;
        warCasualties = w.casualties;
        warWeary = (mine === 'a' ? w.exhaustionA : w.exhaustionB) >= 60;
        const foeSite = this.siteOf(mine === 'a' ? w.defenderSid : w.aggressorSid);
        warFoe = foeSite ? resolvedCityName(foeSite, conn) : 'the enemy town';
        break;
      }
    }
    // Family: household surname.
    let familyLine = '';
    for (const m of this.tbl<MemberRow>('householdMember')) {
      if (m.versonaId !== v.versonaId) continue;
      for (const h of this.tbl<HouseholdRow>('household')) if (h.householdId === m.householdId) { familyLine = `the ${h.surname} household`; break; }
      break;
    }
    // The player, as this town sees them (regard = disposition, lazy-decayed
    // client-side exactly like the server's standingOf read).
    let standing = 10;
    const meHex = atlas.identityHex;
    if (agent && meHex !== null) {
      for (const s of this.tbl<StandingRow>('playerStanding')) {
        if (s.settlementId === agent.settlementId && s.identity.toHexString() === meHex) {
          const ageDays = Math.floor(Number(BigInt(Date.now()) * 1000n - s.updatedAt.microsSinceUnixEpoch) / 86_400_000_000);
          standing = Math.max(0, Math.min(100, s.regard - Math.max(0, ageDays)));
          break;
        }
      }
    }
    // Civ allegiance (2026-08-23): a pilot SWORN to this town's civilization
    // is kin — versonas warm to them (+12 disposition: friendlier tiers, more
    // gated topics open); sworn to the rival reads as −12 (curt lines, thinner
    // answers). The shift rides the SAME regard number every tier, gate and
    // greeting already keys off, so warmth and information depth move together.
    const pilotRow = conn?.identity ? conn.db.pilotState.identity.find(conn.identity) : null;
    const sworn = pilotRow?.factionKey ?? '';
    if (sworn !== '' && civ !== null) {
      const townFaction = civ === 'b' ? 'borean' : 'aurelian';
      standing = Math.max(0, Math.min(100, standing + (sworn === townFaction ? 12 : -12)));
    }
    // Cognition wave: beliefs (diary/bond/player opinion), inner voice, and
    // witness memories ranked recency-x-importance (the retrieval-lite mirror).
    let diaryLine = '';
    let diaryId = -1n;
    let bondLine = '';
    let bondBest = 0;
    let playerOpinion = 0;
    try {
      for (const b of this.tbl<BeliefRow>('versonaBelief')) {
        if (b.versonaId !== v.versonaId) continue;
        if (b.kind === 'reflection' && b.beliefId > diaryId) { diaryId = b.beliefId; diaryLine = b.text; }
        else if (b.kind === 'bond' && Math.abs(b.weight) > bondBest) { bondBest = Math.abs(b.weight); bondLine = b.text; }
        else if (b.kind === 'player' && meHex !== null && b.subjectKey === meHex) playerOpinion = b.weight;
      }
    } catch { /* pre-publish server */ }
    let thought = '';
    try {
      for (const t of this.tbl<ThoughtRow>('versonaThought')) if (t.versonaId === v.versonaId) { thought = t.text; break; }
    } catch { /* pre-publish server */ }
    const witnessRows: Array<{ s: number; text: string }> = [];
    for (const m of this.tbl<MemoryRow>('versonaMemory')) {
      if (m.versonaId !== v.versonaId || m.kind !== 'witness') continue;
      const hours = Math.max(0, (Date.now() - Number(m.at.microsSinceUnixEpoch / 1000n)) / 3.6e6);
      witnessRows.push({ s: Math.pow(0.995, hours) * m.importance, text: m.text });
    }
    witnessRows.sort((x, y) => y.s - x.s);
    const witnessLines = witnessRows.slice(0, 3).map((w) => w.text);
    // Relationship wave: MY relation with this versona (own-rows subs).
    interface RelRow { relId: bigint; versonaId: bigint; identity: { toHexString(): string }; seenCount: number; talkStreak: number; nemesis: boolean; rival: boolean }
    interface RelEvRow { eventId: bigint; versonaId: bigint; identity: { toHexString(): string }; label: string; weight: number; halfLifeS: number; at: { microsSinceUnixEpoch: bigint } }
    interface AspRow { versonaId: bigint; kind: string }
    const nowMs = Date.now();
    let relRow: RelRow | null = null;
    const relEvs: RelEvRow[] = [];
    try {
      for (const r of this.tbl<RelRow>('playerRelation')) {
        if (r.versonaId === v.versonaId && meHex !== null && r.identity.toHexString() === meHex) { relRow = r; break; }
      }
      for (const e of this.tbl<RelEvRow>('playerRelationEvent')) {
        if (e.versonaId === v.versonaId && meHex !== null && e.identity.toHexString() === meHex) relEvs.push(e);
      }
    } catch { /* pre-publish server */ }
    const relNet = relNetOfC(relEvs, nowMs);
    const relTier = tierOfC(relNet, relRow?.seenCount ?? 0, relRow?.nemesis ?? false);
    const disposition = dispositionOfC(relEvs, nowMs); // the four axes behind the tier (2026-09-15)
    const relWhys = relEvs
      .map((e) => ({ e, a: Math.abs(decayedW(e, nowMs)) }))
      .sort((x, y) => y.a - x.a)
      .slice(0, 3)
      .map(({ e }) => ({ label: e.label, weight: e.weight, ago: agoBucket(e.at.microsSinceUnixEpoch, nowMs) }));
    let aspirationLine = '';
    try {
      for (const asp of this.tbl<AspRow>('versonaAspiration')) {
        if (asp.versonaId === v.versonaId) { aspirationLine = ASPIRATION_LABEL[asp.kind] ?? ''; break; }
      }
    } catch { /* pre-publish server */ }
    let isResident = false;
    if (agent && meHex !== null) {
      for (const r of this.tbl<ResidencyRow>('residency')) {
        if (r.identity.toHexString() === meHex && r.settlementId === agent.settlementId) { isResident = true; break; }
      }
    }
    const hot = [...this.tbl<HotRow>('versonaState')].find((h) => h.versonaId === v.versonaId);
    const prof = this.customProfile(v.versonaId);
    return {
      name: prof?.name || nameFromTables(conn, v.versonaId),
      job: agent?.job ?? v.archetype,
      state: hot?.state ?? v.state,
      civName: civInfo?.name ?? '',
      civDemonym: civInfo?.demonym ?? '',
      rivalDemonym: civInfo ? (civInfo.demonym === 'Aurelian' ? 'Borean' : 'Aurelian') : '',
      workplace: civInfo && agent ? `the ${civInfo.demonym} ${WORKPLACE_NOUN[agent.job] ?? 'commons'}` : '',
      town, venue, venueFn,
      tod: todBucket(tod),
      weather,
      hungry: (agent?.hunger ?? 0) > 65,
      happy: (agent?.mood ?? 50) >= 60,
      grumpy: (agent?.mood ?? 50) <= 35,
      feelingTone, feelingCause, memoryLine, rumors,
      townNews: news,
      familyLine,
      foodPrice: econ?.priceFood ?? 4,
      taxPct: Math.round((econ?.taxBps ?? 300) / 100),
      project: econ ? (PROJECT_LABELS[econ.projectKey] ?? '') : '',
      custom,
      mayorName, isMayor: econ !== null && econ.mayorId === v.versonaId,
      keeperName, guildmasterName,
      atWar, warFoe, warCasualties, warWeary,
      playerName: atlas.username ?? 'stranger',
      standing,
      chattedBefore: this.met.has(v.versonaId.toString()),
      relTier,
      disposition,
      relNet,
      seenCount: relRow?.seenCount ?? 0,
      talkStreak: relRow?.talkStreak ?? 0,
      rivalFlag: relRow?.rival ?? false,
      relWhys,
      aspirationLine,
      thought,
      diaryLine,
      bondLine,
      witnessLines,
      playerOpinion,
      isResident,
    };
  }

  /** The dialogue-grounding tables are subscribed lazily on the FIRST talk so
   *  idle clients never pay the replication storm (the join-freeze lesson).
   *  versona_memory / versona_emotion are NOT here: whole-table they are a
   *  30k+-row sync (>60 s even on a raw node socket — measured) that starved
   *  npc_agent behind them; they ride NARROW per-NPC subscriptions instead. */
  private societySubs = false;
  private ensureSocietySubs(): void {
    if (this.societySubs) return;
    this.societySubs = true;
    try {
      atlas.conn?.subscriptionBuilder().subscribe([
        'SELECT * FROM npc_agent', 'SELECT * FROM settlement_norm',
        // V1 names: civ flavor + household surnames for the Talk header.
        'SELECT * FROM settlement_civ', 'SELECT * FROM household', 'SELECT * FROM household_member',
        'SELECT * FROM charter_clause',
        // npc_event is NOT here any more: the global set was a 10k-row initial
        // sync on first Talk (perf sweep 2026-08-22) read only for "the latest
        // line in THIS town" — ensureNpcSubs subscribes the NPC's settlement.
        // Morrowind-topic awareness: disposition, the war, the offices, residency.
        'SELECT * FROM player_standing', 'SELECT * FROM war_state',
        // Relationship wave: MY rows only (the own-rows law) + my resources
        // for the gift picker.
        'SELECT * FROM player_relation WHERE identity = :sender',
        'SELECT * FROM player_relation_event WHERE identity = :sender',
        'SELECT * FROM player_favor WHERE identity = :sender',
        'SELECT * FROM player_resource WHERE owner = :sender',
        'SELECT * FROM settlement_office', 'SELECT * FROM residency',
        'SELECT * FROM guild', 'SELECT * FROM guild_member', 'SELECT * FROM guild_versona',
      ]);
    } catch { /* pre-publish server */ }
  }

  /** One tiny subscription per conversation partner: THEIR memories (the
   *  rumors they carry) + THEIR emotion episodes. Latched per versona —
   *  subscribe() calls stack, so repeats would accumulate. */
  private npcSubs = new Map<string, { unsubscribe(): void } | null>();
  /** Opens waiting on a per-NPC subscription that has not applied yet (the onApplied fans out to all of them). */
  private readonly npcSubWaiters = new Map<string, Array<() => void>>();
  /** Settlements whose npc_event feed is subscribed (one narrow, indexed query each). */
  private sidSubs = new Map<string, { unsubscribe(): void } | null>();
  /** Bound on live per-NPC / per-settlement subscriptions (LRU beyond this is
   *  released): they used to grow for the whole session — ~170 live queries
   *  after a long session, every one re-evaluated per sim transaction. */
  private static readonly MAX_NPC_SUBS = 8;
  private static readonly MAX_SID_SUBS = 4;
  private ensureNpcSubs(versonaId: bigint, settlementId: bigint, onApplied?: () => void): boolean {
    const key = versonaId.toString();
    let fresh = false;
    // Still applying from an earlier open: this open waits for the same rows (2026-09-23 — it started at once, before
    // the memories / provenance / lean it picks an opener from had landed).
    const waiting = this.npcSubWaiters.get(key);
    if (this.npcSubs.has(key) && waiting) { if (onApplied) waiting.push(onApplied); fresh = true; }
    if (!this.npcSubs.has(key)) {
      fresh = true;
      const waiters: Array<() => void> = onApplied ? [onApplied] : [];
      this.npcSubWaiters.set(key, waiters);
      let handle: { unsubscribe(): void } | null = null;
      try {
        handle = atlas.conn?.subscriptionBuilder().onApplied(() => { this.npcSubWaiters.delete(key); for (const w of waiters) w(); }).subscribe([
          `SELECT * FROM versona_memory WHERE versona_id = ${versonaId}`,
          `SELECT * FROM versona_emotion WHERE versona_id = ${versonaId}`,
          // Cognition wave: the semantic layer + inner voice (narrow, per-NPC).
          `SELECT * FROM versona_belief WHERE versona_id = ${versonaId}`,
          `SELECT * FROM versona_thought WHERE versona_id = ${versonaId}`,
          `SELECT * FROM player_favor WHERE versona_id = ${versonaId}`,
          `SELECT * FROM versona_aspiration WHERE versona_id = ${versonaId}`,
          `SELECT * FROM versona_chat WHERE versona_id = ${versonaId}`,
          // Dialogue paths: where their rumors came from, where they stand, what they are called.
          `SELECT * FROM memory_provenance WHERE versona_id = ${versonaId}`,
          `SELECT * FROM versona_opinion WHERE versona_id = ${versonaId}`,
          `SELECT * FROM npc_skill WHERE versona_id = ${versonaId}`,
        ]) as { unsubscribe(): void } | null;
      } catch { this.npcSubWaiters.delete(key); /* pre-publish server — the 1.5 s fallback starts the paths */ }
      this.npcSubs.set(key, handle);
      this.trimSubs(this.npcSubs, NpcInteractModule.MAX_NPC_SUBS);
    }
    // The town's news feed, per settlement (btree npcEventBySettlement) — not the whole world's.
    const sk = settlementId.toString();
    if (settlementId !== 0n && !this.sidSubs.has(sk)) {
      let handle: { unsubscribe(): void } | null = null;
      try {
        handle = atlas.conn?.subscriptionBuilder().subscribe([`SELECT * FROM npc_event WHERE settlement_id = ${settlementId}`]) as { unsubscribe(): void } | null;
      } catch { /* pre-publish server */ }
      this.sidSubs.set(sk, handle);
      this.trimSubs(this.sidSubs, NpcInteractModule.MAX_SID_SUBS);
    }
    return fresh;
  }

  /** Release the oldest subscriptions beyond `max` (Map insertion order = LRU). */
  private trimSubs(map: Map<string, { unsubscribe(): void } | null>, max: number): void {
    while (map.size > max) {
      const oldest = map.keys().next().value as string;
      const h = map.get(oldest);
      map.delete(oldest);
      try { h?.unsubscribe(); } catch { /* already gone */ }
    }
  }

  /** Drop every per-NPC / per-settlement subscription (world hop / reconnect). */
  private clearNpcSubs(): void {
    for (const h of this.npcSubs.values()) { try { h?.unsubscribe(); } catch { /* gone */ } }
    for (const h of this.sidSubs.values()) { try { h?.unsubscribe(); } catch { /* gone */ } }
    this.npcSubs.clear();
    this.sidSubs.clear();
  }

  // ── UI — the Morrowind window ───────────────────────────────────────────────
  /**
   * The versona the player is ACTIVELY talking to (string id), or null.
   * Derived from the window's real open state so no close path (bye button,
   * ✕, Esc, programmatic) can leak a stale "conversing" flag — the versona
   * life renderer reads this each frame to hold the partner still and face
   * the player.
   */
  get activeConversation(): string | null {
    return this.win?.isOpen() && this.current !== null ? this.current.toString() : null;
  }

  /** The open partner's visible mood tone ('' | angry | glad | uneasy | down) —
   *  the life renderer colors the talking pose with it (cognition wave). */
  get partnerMoodHint(): string {
    return this.win?.isOpen() ? this.lastTone : '';
  }
  private lastTone = '';

  /** Each open is a generation: only the NEWEST open may start its paths (2026-09-23 — a first meeting's deferred start
   *  fired after a quick re-press and ran begin() twice: two openers, two visits, and a second speak() cutting the first). */
  private openGen = 0;
  private open(v: VersonaRow): void {
    // T on the partner already talking to you does nothing (2026-09-23): every press re-ran begin() — a visit counted, the
    // next opener marked seen and its question recorded asked, the chips wiped. Holding T walked through the whole bank.
    if (this.win?.isOpen() && this.current === v.versonaId) return;
    const gen = ++this.openGen;
    this.ensureSocietySubs();
    // (bug sweep) settlement from npc_agent — versona.homeCityId is 0n for
    // every townsperson (only the admin assign lever ever writes it), so the
    // per-settlement news sub NEVER fired and "Latest word" was always quiet.
    let sidOfNpc = 0n;
    for (const ag of this.tbl<{ versonaId: bigint; settlementId: bigint }>('npcAgent')) {
      if (ag.versonaId === v.versonaId) { sidOfNpc = ag.settlementId; break; }
    }
    // DIALOGUE PATHS: the opener reads THEIR memories, provenance and lean — rows that ride the narrow sub
    // issued here. On a first meeting they have not landed yet, so the opener (and the VOICE, which must
    // carry greeting + opener as one utterance) waits for the sub to apply — capped, never for the greeting's text.
    let startPaths: (() => void) | null = null;
    const fresh = this.ensureNpcSubs(v.versonaId, sidOfNpc, () => startPaths?.());
    this.current = v.versonaId;
    if (!this.customProfile(v.versonaId)) this.threads?.bind(v.versonaId);
    this.renderedChat.clear();
    // VERSONA VOICES (2026-09-10): a new partner silences the last one; the
    // KittenTTS worker warms while the greeting renders (the model is 24 MB).
    versonaTts().stop();
    versonaTts().prewarm();
    if (!this.win) this.buildWindow();
    const f = this.buildFacts(v);
    this.renderHeader(f, v);
    if (this.log) this.log.textContent = '';
    this.win!.setOpen(true);
    const prof = this.customProfile(v.versonaId);
    if (prof) {
      // An editor-authored versona: its greeting and topics ARE its world —
      // none of the Commons lore/society topics apply here.
      this.threads?.close(); // an authored versona's topics ARE its world — no Commons paths
      this.npcParagraph(prof.greeting || `${prof.name || 'Hello'}. ${prof.context ? 'Ask me what you like.' : ''}`.trim(), null, undefined);
      this.renderCustomTopics(prof);
    } else {
      // The greeting — priority-laddered, context-aware, with topic hooks linked. Rendered now, VOICED with the opener.
      const greeting = composeGreeting(f, this.nextBag(v.versonaId));
      this.npcParagraph(greeting, f, undefined, false);
      this.renderTopics(f);
      let started = false;
      startPaths = (): void => {
        if (started || gen !== this.openGen || this.current !== v.versonaId || !this.win?.isOpen()) return;
        started = true;
        const f2 = this.buildFacts(v);
        const opener = this.threads ? this.threads.begin(this.threads.facts(f2, v.versonaId), v.versonaId) : '';
        this.speakParagraph(v, opener !== '' ? `${greeting} ${opener}` : greeting);
        this.renderTopics(f2);
      };
      if (fresh) this.timer(() => startPaths?.(), 1500);
      else startPaths();
    }
    // Recognition memory: compute chattedBefore BEFORE recording this visit.
    const key = v.versonaId.toString();
    if (!this.met.has(key)) { this.met.add(key); saveSet('atlas:met', this.met, 300); }
    // replay recent replicated chat with this NPC — history is read, never spoken
    this.replaying = true;
    try { for (const c of this.tbl<ChatRow>('versonaChat')) if (c.versonaId === v.versonaId) this.onChatRow(c); } finally { this.replaying = false; }
  }
  private replaying = false;

  /**
   * Read a versona's paragraph aloud in the voice its BODY earns (the same
   * `avatarFor` pick the crowd draws, so the voice matches the face) —
   * KittenTTS in a worker, through the sfx stage (lib/tts/versona_tts).
   */
  private speakParagraph(v: VersonaRow, text: string): void {
    const tts = versonaTts();
    if (!tts.enabled()) return;
    let civ: Civilization | null = null;
    for (const ag of this.tbl<{ versonaId: bigint; settlementId: bigint }>('npcAgent')) {
      if (ag.versonaId !== v.versonaId) continue;
      const site = this.siteOf(ag.settlementId);
      civ = site ? civOfSettlementRowC(ag.settlementId, site.siteSeed) : null;
      break;
    }
    // The voice follows the body DRAWN: a wave-4 body streams in on demand and its wearer stands in the pre-wave body
    // for a moment (versona_bodies) — until it lands the voice is that body's, never a voice for a face not shown.
    const env = this.app?.getModule('Environment') as { bodyKeyOf?: (id: string) => string | null } | undefined;
    const body = env?.bodyKeyOf?.(v.versonaId.toString()) || avatarFor(v.archetype, v.versonaId, civ);
    tts.speak(v.versonaId.toString(), text, voiceFor(body, v.versonaId));
  }

  private buildWindow(): void {
    this.win = makeWindow({ id: 'npc-talk', title: 'Conversation', width: 620, height: 440, minWidth: 460, minHeight: 320 });
    const p = document.createElement('div');
    p.style.cssText = 'display:flex;flex-direction:column;height:100%;padding:8px;gap:6px;color:var(--ax-text);font:12px/1.5 var(--ax-font);box-sizing:border-box';
    this.win.body.appendChild(p);
    // Header: name/job/doing + disposition (Morrowind puts it under the name).
    const headRow = document.createElement('div');
    headRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px';
    this.head = document.createElement('div');
    this.head.style.cssText = 'font-weight:600;color:var(--ax-text);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
    headRow.appendChild(this.head);
    const disp = document.createElement('div');
    disp.title = 'Disposition — how this town regards you (earned by honest trade, residency, and conversation)';
    disp.style.cssText = 'display:flex;align-items:center;gap:6px;flex:none';
    this.dispLabel = document.createElement('span');
    this.dispLabel.id = 'npc-talk-disp';
    this.dispLabel.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-font)';
    const bar = document.createElement('div');
    bar.style.cssText = 'width:90px;height:8px;border-radius:var(--ax-radius);background:var(--ax-bg2);border:1px solid var(--ax-line2);overflow:hidden';
    this.dispFill = document.createElement('div');
    this.dispFill.style.cssText = 'height:100%;width:10%;background:linear-gradient(90deg,color-mix(in srgb, var(--ax-warn) 48%, var(--ax-bg2)),var(--ax-accent));transition:width .3s';
    bar.appendChild(this.dispFill);
    disp.appendChild(this.dispLabel);
    disp.appendChild(bar);
    headRow.appendChild(disp);
    p.appendChild(headRow);
    // Main: dialogue text left, topic column right — the TES III layout.
    const main = document.createElement('div');
    main.style.cssText = 'flex:1;display:flex;gap:8px;min-height:0';
    this.log = document.createElement('div');
    this.log.id = 'npc-talk-log';
    this.log.style.cssText = 'flex:1;overflow:auto;display:flex;flex-direction:column;gap:7px;padding:6px 8px;'
      + 'background:color-mix(in srgb, var(--ax-bg) 45%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-radius);scrollbar-width:thin';
    main.appendChild(this.log);
    this.topicsPane = document.createElement('div');
    this.topicsPane.id = 'npc-talk-topics';
    this.topicsPane.style.cssText = 'width:152px;flex:none;overflow:auto;display:flex;flex-direction:column;gap:2px;'
      + 'padding:4px;background:color-mix(in srgb, var(--ax-bg) 45%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-radius);scrollbar-width:thin';
    main.appendChild(this.topicsPane);
    p.appendChild(main);
    // Footer: free-text ask (NLP → topics; unmatched → the town AI).
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:5px';
    this.input = document.createElement('input');
    this.input.id = 'npc-talk-input';
    this.input.placeholder = 'Ask about anything…';
    this.input.style.cssText = 'flex:1;background:var(--ax-bg2);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:5px';
    this.input.onkeydown = (e) => { e.stopPropagation(); if (e.key === 'Enter') this.sendFreeText(); if (e.key === 'Escape') this.input?.blur(); };
    row.appendChild(this.input);
    const send = document.createElement('button');
    send.textContent = 'Say';
    send.style.cssText = 'padding:5px 10px;background:color-mix(in srgb, var(--ax-good) 22%, var(--ax-bg2));color:var(--ax-text);border:1px solid color-mix(in srgb, var(--ax-good) 34%, var(--ax-line));border-radius:var(--ax-radius);cursor:pointer';
    send.onclick = () => this.sendFreeText();
    row.appendChild(send);
    p.appendChild(row);
  }

  private renderHeader(f: TopicFacts, v: VersonaRow): void {
    const prof = this.customProfile(v.versonaId);
    if (this.head) {
      const doing = f.state !== 'idle' && f.state !== '' ? ` · ${f.state.replace(/_/g, ' ')}` : '';
      // Merchant slice (2026-09-15): the counter's phase, from the durable row —
      // "serving you" / "handing you the goods" / "serving a customer".
      const svc = [...this.tbl<{ versonaId: bigint; customer: { isEqual(o: unknown): boolean }; phase: string }>('merchantService')].find((s) => s.versonaId === v.versonaId);
      const meId = atlas.conn?.identity;
      const counter = svc && svc.phase !== 'idle'
        ? (meId && svc.customer.isEqual(meId) ? (svc.phase === 'handover' ? ' · handing you the goods' : ' · serving you') : ' · serving a customer')
        : '';
      this.head.textContent = prof ? `${f.name}${doing}${counter}` : `${f.name} — ${f.job}${f.isMayor ? ' · KEEPER' : ''}${doing}${counter}`;
      this.head.title = `#${v.versonaId}${f.town ? ` of ${f.town}` : ''}`;
    }
    // Disposition is a settlement notion — an authored versona has none.
    if (this.dispFill) { this.dispFill.style.width = `${Math.max(4, f.standing)}%`; this.dispFill.parentElement!.style.visibility = prof ? 'hidden' : 'visible'; }
    if (this.dispLabel) this.dispLabel.textContent = prof ? '' : `${f.standing} · ${standingTierOf(f.standing)} · ${f.relTier}`;
  }

  /** The topic column for an editor-authored versona: its own topics + Goodbye. */
  private renderCustomTopics(p: ProfileRow): void {
    const pane = this.topicsPane;
    if (!pane) return;
    pane.textContent = '';
    const cap = document.createElement('div');
    cap.textContent = 'TOPICS';
    cap.style.cssText = 'color:var(--ax-muted);font:600 10px var(--ax-font);letter-spacing:1px;padding:2px 4px';
    pane.appendChild(cap);
    for (const t of this.customTopics(p)) {
      const b = document.createElement('button');
      b.textContent = t.l;
      b.dataset.topic = t.k;
      b.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-hi)';
      b.onmouseenter = () => { b.style.background = 'var(--ax-bg3)'; };
      b.onmouseleave = () => { b.style.background = 'none'; };
      b.onclick = () => { this.playerLine(t.l); this.npcParagraph(t.r, null, undefined); };
      pane.appendChild(b);
    }
    const gap = document.createElement('div');
    gap.style.cssText = 'flex:1';
    pane.appendChild(gap);
    this.justDiscovered = ''; // (bug sweep) the gold highlight is one render, not forever
    const bye = document.createElement('button');
    bye.textContent = 'Goodbye';
    bye.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:600 12px var(--ax-font);color:var(--ax-text)';
    bye.onclick = () => { this.win?.setOpen(false); this.current = null; this.threads?.close(); versonaTts().stop(); };
    pane.appendChild(bye);
  }

  /** The topic column: known base topics ∪ discovered, rumors pinned first,
   *  Whisper-a-tale + Goodbye at the bottom (Morrowind's column order). */
  private renderTopics(f: TopicFacts): void {
    const pane = this.topicsPane;
    if (!pane) return;
    pane.textContent = '';
    const cap = document.createElement('div');
    cap.textContent = 'TOPICS';
    cap.style.cssText = 'color:var(--ax-muted);font:600 10px var(--ax-font);letter-spacing:1px;padding:2px 4px';
    pane.appendChild(cap);
    for (const t of availableTopics(f, this.discovered)) {
      const b = document.createElement('button');
      b.textContent = t.label;
      b.dataset.topic = t.key;
      const fresh = t.key === this.justDiscovered;
      b.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;'
        + `font:12px var(--ax-font);color:${fresh ? 'var(--ax-hi)' : 'var(--ax-hi)'};`;
      b.onmouseenter = () => { b.style.background = 'var(--ax-bg3)'; };
      b.onmouseleave = () => { b.style.background = 'none'; };
      b.onclick = () => this.ask(t.key);
      pane.appendChild(b);
    }
    const gap = document.createElement('div');
    gap.style.cssText = 'flex:1';
    pane.appendChild(gap);
    // DIALOGUE PATHS — leads pointing at THIS versona, their story, the tale satchel, the journal.
    if (this.threads && this.current !== null) this.threads.renderActions(pane, this.threads.facts(f, this.current));
    // V13 — plant a tale: it lands as a rumor and TRAVELS (retold, mutated,
    // credibility falling) like any other story. One per day.
    // ── Relationship actions (relationship wave) ──
    const actCss = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-soft)';
    interface FavRow { favorId: bigint; versonaId: bigint; identity: { toHexString(): string }; kind: string; targetKey: string; qty: number; detail: string; state: string }
    interface ResRow { stackId: bigint; owner: { toHexString(): string }; familyKey: string; qty: number }
    const meHex2 = atlas.conn?.identity?.toHexString() ?? null;
    // A versona's OPEN ask → Accept; my accepted delivery from them → Deliver.
    try {
      for (const fav of this.tbl<FavRow>('playerFavor')) {
        if (fav.versonaId !== (this.current ?? -1n)) continue;
        if (fav.state === 'offered' && f.relTier !== 'hostile' && f.relTier !== 'nemesis') {
          const b1 = document.createElement('button');
          b1.textContent = `Help: ${fav.detail.slice(0, 46)}…`;
          b1.title = fav.detail;
          b1.style.cssText = actCss;
          b1.onclick = () => {
            const r = atlas.conn?.reducers as unknown as { acceptFavor?(a: { favorId: bigint }): Promise<void> } | undefined;
            void r?.acceptFavor?.({ favorId: fav.favorId })
              .then(() => this.systemLine('(you take the task on — they look relieved)'))
              .catch((e: Error) => this.systemLine(`(${e.message})`));
          };
          pane.appendChild(b1);
        } else if (fav.state === 'accepted' && meHex2 !== null && fav.identity.toHexString() === meHex2 && (fav.kind === 'bring_good' || fav.kind === 'amends')) {
          const b2 = document.createElement('button');
          b2.textContent = `Deliver ${fav.qty} ${fav.targetKey.replace(/_/g, ' ')}`;
          b2.title = fav.detail;
          b2.style.cssText = actCss;
          b2.onclick = () => {
            const r = atlas.conn?.reducers as unknown as { fulfillFavor?(a: { favorId: bigint }): Promise<void> } | undefined;
            void r?.fulfillFavor?.({ favorId: fav.favorId })
              .then(() => this.systemLine(fav.kind === 'amends' ? '(something in their face unclenches — amends, made real)' : '(delivered — they will remember this)'))
              .catch((e: Error) => this.systemLine(`(${e.message})`));
          };
          pane.appendChild(b2);
        }
      }
    } catch { /* pre-publish server */ }
    // Give a gift — one a day; hostile hands are refused server-side too.
    if (f.relTier !== 'hostile' && f.relTier !== 'nemesis') {
      const gift = document.createElement('button');
      gift.textContent = 'Give a gift…';
      gift.title = 'One a day. Taste matters: think about their trade and their dreams.';
      gift.style.cssText = actCss;
      gift.onclick = () => {
        // Inline DOM picker (bug sweep): a synchronous prompt() froze rAF and
        // the WS pump for as long as the dialog stood, and dropped pointer lock.
        const prev = pane.querySelector('.gift-pick');
        if (prev) { prev.remove(); return; }
        try {
          const mine = [...this.tbl<ResRow>('playerResource')].filter((s2) => meHex2 !== null && s2.owner.toHexString() === meHex2 && s2.qty > 0);
          if (mine.length === 0) { this.systemLine('(you carry nothing worth giving — gather some resources first)'); return; }
          const box = document.createElement('div');
          box.className = 'gift-pick';
          box.style.cssText = 'display:flex;flex-direction:column;margin:2px 0 2px 14px;border-left:1px solid var(--ax-line);padding-left:6px';
          for (const s2 of mine.slice(0, 9)) {
            const opt = document.createElement('button');
            opt.textContent = `${s2.familyKey.replace(/_/g, ' ')} ×${s2.qty}`;
            opt.style.cssText = actCss;
            opt.onclick = () => {
              box.remove();
              const r = atlas.conn?.reducers as unknown as { giveGiftToVersona?(a: { versonaId: bigint; stackId: bigint }): Promise<void> } | undefined;
              if (this.current !== null) {
                void r?.giveGiftToVersona?.({ versonaId: this.current, stackId: s2.stackId })
                  .then(() => this.systemLine('(they turn the gift over in their hands)'))
                  .catch((e: Error) => this.systemLine(`(${e.message})`));
              }
            };
            box.appendChild(opt);
          }
          gift.after(box);
        } catch { /* pre-publish server */ }
      };
      pane.appendChild(gift);
    }
    // Apologize — only when there is something to mend.
    if (f.relNet < -5) {
      const sorry = document.createElement('button');
      sorry.textContent = 'Apologize';
      sorry.title = 'Own what you did. Some will hear it; the proud will not — deeds may be asked of you.';
      sorry.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-soft)';
      sorry.onclick = () => {
        const r = atlas.conn?.reducers as unknown as { apologizeToVersona?(a: { versonaId: bigint }): Promise<void> } | undefined;
        if (this.current !== null) {
          void r?.apologizeToVersona?.({ versonaId: this.current })
            .then(() => this.systemLine('(you say your piece — how it lands is written on their face)'))
            .catch((e: Error) => this.systemLine(`(${e.message})`));
        }
      };
      pane.appendChild(sorry);
    }
    // Recruit into the player's guild — only a true FRIEND will take the oath.
    {
      const meHex3 = atlas.conn?.identity?.toHexString() ?? null;
      const myMembership = meHex3 === null ? undefined
        : [...this.tbl<{ guildId: bigint; identity: { toHexString(): string } }>('guildMember')].find((m) => m.identity.toHexString() === meHex3);
      const alreadyServing = this.current !== null
        && [...this.tbl<{ versonaId: bigint }>('guildVersona')].some((v) => v.versonaId === this.current);
      if (myMembership && !alreadyServing && f.relTier !== 'hostile' && f.relTier !== 'nemesis') {
        const guildRow = [...this.tbl<{ guildId: bigint; name: string; tag: string }>('guild')].find((g) => g.guildId === myMembership.guildId);
        const high = f.relNet >= 30; // FRIEND_NET mirror — the server re-checks
        const rec = document.createElement('button');
        rec.textContent = high ? `Recruit into ${guildRow?.name ?? 'the guild'}…` : 'Recruit to guild (they must regard you as a FRIEND)';
        rec.title = high ? 'Offer them the guild oath — assign their post from the Guilds window (V)'
          : 'Their regard for you must reach FRIEND first: talk daily, give thoughtful gifts, do their favors.';
        rec.style.cssText = actCss;
        if (!high) { rec.style.opacity = '0.5'; rec.style.cursor = 'default'; }
        rec.onclick = () => {
          if (!high || this.current === null) return;
          const r = atlas.conn?.reducers as unknown as { recruitGuildVersona?(a: { versonaId: bigint }): Promise<void> } | undefined;
          void r?.recruitGuildVersona?.({ versonaId: this.current })
            .then(() => this.systemLine('(they take the oath — assign their post from the Guilds window, V)'))
            .catch((e: Error) => this.systemLine(`(${e.message})`));
        };
        pane.appendChild(rec);
      }
    }
    const tale = document.createElement('button');
    tale.textContent = 'Whisper a tale…';
    tale.title = 'Plant a rumor — the town will retell it, and it will drift (one per day)';
    tale.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-bad)';
    tale.onclick = () => {
      // Inline input (bug sweep) — prompt() froze the frame loop; see the gift picker.
      const prev = pane.querySelector('.tale-pick');
      if (prev) { prev.remove(); return; }
      const row = document.createElement('div');
      row.className = 'tale-pick';
      row.style.cssText = 'display:flex;gap:4px;margin:2px 0 2px 14px';
      const inp = document.createElement('input');
      inp.maxLength = 100;
      inp.placeholder = 'a tale they may pass on…';
      inp.style.cssText = 'flex:1;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);font:12px var(--ax-font);padding:2px 6px';
      const go = document.createElement('button');
      go.textContent = 'Whisper';
      go.style.cssText = 'padding:2px 8px;background:var(--ax-bg3);border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-bad)';
      const send = (): void => {
        const text = inp.value.trim();
        row.remove();
        if (!text || this.current === null) return;
        const r = atlas.conn?.reducers as unknown as { spreadRumor?(a: { versonaId: bigint; text: string }): Promise<void> } | undefined;
        void r?.spreadRumor?.({ versonaId: this.current, text })
          .then(() => this.systemLine('(they lean in, listening…)'))
          .catch((e: Error) => this.systemLine(`(${e.message})`));
      };
      go.onclick = send;
      inp.onkeydown = (ev) => { if (ev.key === 'Enter') send(); ev.stopPropagation(); };
      row.append(inp, go);
      tale.after(row);
      inp.focus();
    };
    pane.appendChild(tale);
    this.justDiscovered = ''; // (bug sweep) the gold highlight is one render, not forever
    const bye = document.createElement('button');
    bye.textContent = 'Goodbye';
    bye.style.cssText = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:600 12px var(--ax-font);color:var(--ax-text)';
    bye.onclick = () => { this.win?.setOpen(false); this.current = null; this.threads?.close(); versonaTts().stop(); };
    pane.appendChild(bye);
  }

  private nextBag(id: bigint): number {
    this.bag++;
    return fnv(`${id}|${this.bag}`);
  }

  private currentRow(): VersonaRow | null {
    const conn = atlas.conn;
    if (!conn || this.current === null) return null;
    for (const row of conn.db.versona.iter() as Iterable<VersonaRow>) if (row.versonaId === this.current) return row;
    return null;
  }

  /** Click/typed topic → instant salience answer, links scanned in. */
  private ask(topicKey: string): void {
    const v = this.currentRow();
    if (!v) return;
    const f = this.buildFacts(v);
    const label = topicByKey(topicKey)?.label ?? topicKey;
    this.threads?.dismiss(); // changing the subject leaves their question hanging — and that is remembered
    this.playerLine(label);
    const answer = respond(topicKey, f, this.nextBag(v.versonaId));
    this.npcParagraph(answer, f, topicKey);
    // The newest rumor, told to your face, is a tale you now carry.
    if (this.threads && f.rumors.length > 0 && answer.includes(f.rumors[0])) this.threads.heardRumor(this.threads.facts(f, v.versonaId));
    this.renderHeader(f, v);
  }

  /** A topic became known — Morrowind's growing list (persisted). */
  private discover(topicKey: string): void {
    const t = topicByKey(topicKey);
    if (!t || t.base || this.discovered.has(topicKey)) return;
    this.discovered.add(topicKey);
    saveSet('atlas:topics', this.discovered, 64);
    this.justDiscovered = topicKey;
  }

  private sendFreeText(): void {
    const text = this.input?.value.trim() ?? '';
    if (!text || this.current === null) return;
    if (this.input) this.input.value = '';
    const v = this.currentRow();
    if (!v) return;
    this.threads?.dismiss(); // typing past a question is an answer of its own
    const f = this.buildFacts(v);
    const prof = this.customProfile(v.versonaId);
    if (prof) {
      // Authored versona: its topics first; then the LLM only if the editor
      // allowed it; otherwise the deflect line — deterministic by design.
      this.playerLine(text);
      const hit = this.matchCustomTopic(text, this.customTopics(prof));
      if (hit) { this.npcParagraph(hit.r, null, undefined); return; }
      const deflect = prof.deflect || "I couldn't say. Ask me about one of my topics.";
      if (!prof.llm) { this.npcParagraph(deflect, null, undefined); return; }
      const rr = atlas.conn?.reducers as unknown as { sayToVersona?(a: { versonaId: bigint; text: string }): Promise<void> } | undefined;
      if (typeof rr?.sayToVersona !== 'function') { this.npcParagraph(deflect, null, undefined); return; }
      this.waitingSince = performance.now();
      const asked = this.current;
      this.timer(() => {
        if (this.current === asked && this.waitingSince !== 0 && performance.now() - this.waitingSince > 9500) {
          this.waitingSince = 0;
          this.npcParagraph(deflect, null, undefined);
        }
      }, 10_000);
      try { void rr.sayToVersona({ versonaId: this.current, text }).catch((e: Error) => this.systemLine(`(${e.message})`)); } catch { /* the timer answers */ }
      return;
    }
    // NLP first: stemmed, typo-tolerant keyword match → instant topic answer.
    const m = matchTopic(text, f);
    if (m) {
      this.playerLine(text);
      this.discover(m.key);
      this.npcParagraph(respond(m.key, f, this.nextBag(v.versonaId)), f, m.key);
      this.renderTopics(f);
      return;
    }
    this.playerLine(text);
    const r = atlas.conn?.reducers as unknown as { sayToVersona?(a: { versonaId: bigint; text: string }): Promise<void> } | undefined;
    if (typeof r?.sayToVersona !== 'function') { this.redirectLine(f); return; }
    // Fire the LLM inbox regardless (a live worker may still answer richer),
    // but once we KNOW no LLM is attached, answer instantly with a redirect.
    // The redirect timer is armed BEFORE the send: a wedged socket can make
    // the reducer call throw synchronously (or never settle) — the player
    // still gets an answer either way.
    if (!this.llmDown) {
      this.waitingSince = performance.now();
      const asked = this.current;
      this.timer(() => {
        if (this.current === asked && this.waitingSince !== 0 && performance.now() - this.waitingSince > 9500) {
          this.waitingSince = 0;
          this.llmDown = true; // the fallback latch — later questions redirect instantly
          this.redirectLine(this.buildFacts(this.currentRow() ?? v));
        }
      }, 10_000);
    }
    try {
      void r.sayToVersona({ versonaId: this.current, text }).catch((e: Error) => this.systemLine(`(${e.message})`));
    } catch { /* saturated-tab sync throw — the timer redirect answers */ }
    if (this.llmDown) this.redirectLine(f);
  }

  /** The graceful no-LLM answer: point at real topics instead of shrugging. */
  private redirectLine(f: TopicFacts): void {
    const hints = nearTopics(f, this.discovered);
    const line = hints.length > 0
      ? `Couldn't say, truthfully. Ask me about the ${hints.join(' or the ')} — those I know.`
      : 'Couldn\'t say, truthfully. Try one of the topics.';
    this.npcParagraph(line, f, undefined);
  }

  private onChatRow(row: ChatRow): void {
    if (this.current === null || row.versonaId !== this.current) return;
    const key = row.chatId.toString();
    if (this.renderedChat.has(key)) return;
    this.renderedChat.add(key);
    if (row.speaker === 'npc') {
      this.waitingSince = 0;
      this.llmDown = false; // evidence of a live LLM worker
      const v = this.currentRow();
      const custom = v ? this.customProfile(v.versonaId) : null;
      this.npcParagraph(row.text, v && !custom ? this.buildFacts(v) : null, undefined, !this.replaying);
    } else if (row.speakerName !== (atlas.username ?? '')) {
      this.playerLine(`${row.speakerName}: ${row.text}`);
    }
  }

  // ── xr-ui (2026-09-28): DOM never renders in a headset — the VR panel's Dialogue page mirrors this conversation and
  //    asks through the SAME paths the window's topic buttons, T and Goodbye use. ──
  private xrLastLine = '';
  /** The live conversation (null when none): who, their last line, the topic buttons (`custom:` for an authored versona). */
  xrTalk(): { name: string; line: string; topics: Array<{ key: string; label: string }> } | null {
    const v = this.currentRow();
    if (!v || !this.win?.isOpen()) return null;
    const prof = this.customProfile(v.versonaId);
    const topics = prof
      ? this.customTopics(prof).map((t) => ({ key: `custom:${t.k}`, label: t.l }))
      : availableTopics(this.buildFacts(v), this.discovered).map((t) => ({ key: t.key, label: t.label }));
    return { name: this.head?.textContent ?? '', line: this.xrLastLine, topics };
  }
  /** A topic, exactly as a click on the window's topic list asks it. */
  xrAsk(key: string): void {
    const v = this.currentRow();
    if (!v) return;
    const prof = key.startsWith('custom:') ? this.customProfile(v.versonaId) : null;
    if (prof) {
      const t = this.customTopics(prof).find((x) => `custom:${x.k}` === key);
      if (t) { this.playerLine(t.l); this.npcParagraph(t.r, null, undefined); }
      return;
    }
    this.ask(key);
  }
  /** T's pick (the versona you face within range, else the nearest) — false when nobody is in range. */
  xrTalkNearest(): boolean { const n = this.nearestVersona(); if (n) this.open(n); return !!n; }
  /** The window's Goodbye. */
  xrGoodbye(): void { this.win?.setOpen(false); this.current = null; this.threads?.close(); versonaTts().stop(); }

  // ── log rendering (linkified NPC paragraphs — the blue keywords) ───────────
  private npcParagraph(text: string, f: TopicFacts | null, excludeTopic: string | undefined, spoken = true): void {
    if (!this.log) return;
    this.xrLastLine = text; // xr-ui (2026-09-28): the VR Dialogue page shows the newest line
    if (spoken) { const v = this.currentRow(); if (v) this.speakParagraph(v, text); }
    const d = document.createElement('div');
    d.style.cssText = 'align-self:flex-start;background:var(--ax-bg3);border-radius:var(--ax-radius);padding:5px 8px;max-width:94%;color:var(--ax-text)';
    if (f) {
      for (const seg of linkify(text, f, excludeTopic)) {
        if (seg.kind === 'link' && seg.topicKey) {
          const a = document.createElement('span');
          a.textContent = seg.text;
          const isNew = !this.discovered.has(seg.topicKey) && !(topicByKey(seg.topicKey)?.base ?? false);
          a.style.cssText = `color:${isNew ? 'var(--ax-hi)' : 'var(--ax-hi)'};cursor:pointer;text-decoration:underline;text-underline-offset:2px`;
          a.dataset.link = seg.topicKey;
          const key = seg.topicKey;
          a.onclick = () => {
            this.discover(key);
            const v = this.currentRow();
            if (v) this.renderTopics(this.buildFacts(v));
            this.ask(key);
          };
          d.appendChild(a);
        } else {
          d.appendChild(document.createTextNode(seg.text));
        }
      }
    } else {
      d.textContent = text;
    }
    this.push(d);
  }

  private playerLine(text: string): void {
    if (!this.log) return;
    const d = document.createElement('div');
    d.style.cssText = 'align-self:flex-end;background:var(--ax-bg3);border-radius:var(--ax-radius);padding:5px 8px;max-width:92%';
    d.textContent = text;
    this.push(d);
  }

  private systemLine(text: string): void {
    if (!this.log) return;
    const d = document.createElement('div');
    d.style.cssText = 'align-self:flex-start;color:var(--ax-soft);font-style:italic;padding:0 4px';
    d.textContent = text;
    this.push(d);
  }

  private push(d: HTMLDivElement): void {
    if (!this.log) return;
    this.log.appendChild(d);
    while (this.log.childElementCount > 80) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;
  }

  dispose(): void {
    versonaTts().stop();
    // (bug sweep) the keydown handler, bus handlers, pending timers, the
    // __talk dev global and live subs all used to survive teardown.
    window.removeEventListener('keydown', this.onKeyDownT);
    for (const off of this.offs) { try { off(); } catch { /* gone */ } }
    this.offs = [];
    for (const id of this.timers) window.clearTimeout(id);
    this.timers.clear();
    delete (window as { __talk?: unknown }).__talk;
    this.threads?.dispose();
    this.threads = null;
    this.clearNpcSubs();
    this.win?.root.remove();
    this.win = null;
  }
}
